/**
 * Homepage hero background: one motion loop per live / coming-soon project,
 * crossfading between them. Reuses the per-project hero video (and its
 * poster) that the project landing pages already play — nothing new is
 * uploaded or invented here.
 *
 * Rendering contract:
 *   - The caller paints a solid navy base (#0A2540) with a fixed hero height.
 *     Once slides exist, the old static wellpad photo is not rendered at all,
 *     so crossfades and pre-canplay video never expose it.
 *   - Each slide paints its poster `<img>` before any video. Video `src`
 *     is attached after mount; only the active slide, an outgoing slide
 *     during a crossfade, and the one queued next ever hold a video
 *     element. The queued one only preloads metadata when it has a poster.
 *   - Crossfade holds the outgoing slide at opacity 1 while the incoming
 *     slide fades in on top, then unmounts the old one — never a mutual
 *     1→0 / 0→1 dip that would reveal the navy (or a stale photo).
 *   - Videos are `muted loop playsInline`, start via `play()` when their
 *     slide is active and the hero is on screen, and pause otherwise.
 *   - The crossfade waits for the incoming slide to have something to
 *     show (poster, or first frames when it has no poster).
 *   - Reduced-motion and Save-Data / slow-connection visitors get the
 *     active slide's own poster only: no autoplay, no rotation.
 *   - The copy and CTAs live outside this component on top of its scrim.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import {
  isConstrainedConnection,
  shouldPlayHeroVideo,
} from "@/components/project-hero-video";
import { usePrefersReducedMotion } from "@/components/project-motion";
import { pickProjectImage } from "@/lib/project-image";
import { getClassificationLabel } from "@/lib/project-labels";

/** How long each project loop holds the hero before the crossfade. */
export const HOME_HERO_ROTATE_MS = 9000;
/** Crossfade length; keep in sync with the duration class on the slide. */
export const HOME_HERO_FADE_MS = 1200;
/** The public index is a two-project surface; cap defensively anyway. */
export const HOME_HERO_MAX_SLIDES = 4;

/**
 * Still frames taken from each project's own hero loop. Used when the API
 * poster is missing or would resolve to another project's cover (the
 * wellpad photo must never stand in for Marathon Key).
 */
export const HOME_HERO_LOOP_FRAME_POSTERS: Readonly<Record<string, string>> = {
  marathonkeys: "/images/home/marathon-key-loop-poster.webp",
};

export interface HomeHeroLoopProject {
  id: string;
  title: string;
  slug: string;
  location?: string | null;
  investmentType?: string | null;
  coverImageUrl?: string | null;
  marketingImageDerivatives?: unknown;
  heroVideoUrl?: string | null;
  heroVideoPosterUrl?: string | null;
  heroVideoContentType?: string | null;
  status?: string | null;
}

export interface HomeHeroSlide {
  id: string;
  slug: string;
  title: string;
  location: string | null;
  /** Text badge shown next to the title ("Rural"); never a third video. */
  classification: string | null;
  poster: string | null;
  video: string | null;
  videoType: string | null;
  /** "Coming soon, first look" for coming-soon projects (Marathon Key). */
  statusLabel?: string | null;
}

/**
 * Turn the public projects payload into hero slides. The featured project
 * leads so its poster is the first one visible; projects with neither a
 * hero video nor any cover image are skipped rather than given invented
 * media.
 */
export function buildHomeHeroSlides(
  projects: readonly HomeHeroLoopProject[] | null | undefined,
  featuredSlug?: string | null,
): HomeHeroSlide[] {
  if (!projects || projects.length === 0) return [];
  const ordered = [...projects].sort((a, b) => {
    if (featuredSlug && a.slug === featuredSlug && b.slug !== featuredSlug) return -1;
    if (featuredSlug && b.slug === featuredSlug && a.slug !== featuredSlug) return 1;
    return 0;
  });
  const slides: HomeHeroSlide[] = [];
  for (const project of ordered) {
    if (slides.length >= HOME_HERO_MAX_SLIDES) break;
    const video = project.heroVideoUrl?.trim() || null;
    const loopFrame = HOME_HERO_LOOP_FRAME_POSTERS[project.slug] ?? null;
    const apiPoster = project.heroVideoPosterUrl?.trim() || null;
    // Video slides: prefer a frame of that project's own loop, then the API
    // poster. Never fall through to cover derivatives for video slides —
    // those can resolve to the wrong project's photo (the wellpad).
    // Image-only slides keep the cover_header → thumbnail → cover chain.
    const poster = video
      ? loopFrame || apiPoster || null
      : apiPoster ||
        pickProjectImage(project, "cover_header", { derivativeOnly: true }) ||
        pickProjectImage(project, "cover_thumbnail") ||
        null;
    if (!video && !poster) continue;
    slides.push({
      id: project.id,
      slug: project.slug,
      title: project.title,
      location: project.location?.trim() || null,
      classification: getClassificationLabel(project.investmentType),
      poster,
      video,
      videoType: video ? (project.heroVideoContentType ?? null) : null,
      statusLabel: project.status === "coming_soon" ? "Coming soon, first look" : null,
    });
  }
  return slides;
}

function safePlay(node: HTMLVideoElement) {
  try {
    const result = node.play();
    if (result && typeof (result as Promise<void>).catch === "function") {
      (result as Promise<void>).catch(() => {});
    }
  } catch {
    /* autoplay refused — the poster stays visible */
  }
}

function safePause(node: HTMLVideoElement) {
  try {
    node.pause();
  } catch {
    /* ignore */
  }
}

type SlideRole = "active" | "leaving" | "preload";

function HomeHeroSlideLayer({
  slide,
  role,
  fadeIn,
  playing,
  withVideo,
  stillFrame,
  eagerPoster,
  primePlayback,
  onReady,
}: {
  slide: HomeHeroSlide;
  role: SlideRole;
  /** Incoming slide during a hold-outgoing crossfade: start at 0, rise to 1. */
  fadeIn: boolean;
  /** Active (or still leaving), and the hero is on screen. */
  playing: boolean;
  withVideo: boolean;
  /** Reduced motion with no poster: hold the first frame, never autoplay. */
  stillFrame: boolean;
  eagerPoster: boolean;
  /**
   * Poster-less slide waiting its turn. Playback is kicked (muted) so the
   * first frames decode; a paused element never reaches `canplay` on iOS
   * Safari, which would stall the rotation.
   */
  primePlayback: boolean;
  onReady: (slideId: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [sourceAttached, setSourceAttached] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  // Incoming slide starts transparent and fades up on top of the held
  // outgoing slide. Adjust state during render when fadeIn flips so a
  // preloaded layer does not paint opaque for one frame.
  const [fadeInTracked, setFadeInTracked] = useState(fadeIn);
  const [fadeVisible, setFadeVisible] = useState(!fadeIn);
  if (fadeIn !== fadeInTracked) {
    setFadeInTracked(fadeIn);
    setFadeVisible(!fadeIn);
  }

  useEffect(() => {
    setReady(false);
    setFailed(false);
  }, [slide.video]);

  useEffect(() => {
    if (!fadeIn) return;
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setFadeVisible(true));
    });
    return () => window.cancelAnimationFrame(id);
  }, [fadeIn, slide.id]);

  // Attach the source only after mount so the poster is painted first.
  useEffect(() => {
    if (!withVideo || !slide.video) return;
    const id = window.setTimeout(() => setSourceAttached(true), 0);
    return () => window.clearTimeout(id);
  }, [withVideo, slide.video]);

  const showVideo = withVideo && !!slide.video && sourceAttached && !failed;
  const showStill = !withVideo && stillFrame && !!slide.video && !failed;

  useEffect(() => {
    const node = videoRef.current;
    if (!node || !showVideo) return;
    if (playing || (primePlayback && !ready)) safePlay(node);
    else safePause(node);
  }, [playing, showVideo, primePlayback, ready]);

  // The queued slide only needs metadata when its poster can carry the
  // crossfade; without a poster it buffers so first frames are ready.
  const queuedPreload = role === "active" || role === "leaving" || !slide.poster ? "auto" : "metadata";

  const markReady = () => {
    setReady(true);
    onReady(slide.id);
  };
  const markFailed = () => {
    setFailed(true);
    // A broken file must not stall the rotation; the slide's own poster
    // (or the navy base underneath) stands in — never a foreign photo.
    onReady(slide.id);
  };

  const opaque =
    role === "leaving" || (role === "active" && fadeVisible);
  const zClass =
    role === "active" ? "z-20" : role === "leaving" ? "z-10" : "z-0";

  return (
    <div
      className={`absolute inset-0 ${zClass} transition-opacity duration-[1200ms] ease-in-out motion-reduce:transition-none ${
        opaque ? "opacity-100" : "opacity-0"
      }`}
      data-testid={`hero-loop-slide-${slide.slug}`}
      data-active={role === "active" ? "true" : "false"}
      data-role={role}
    >
      {slide.poster ? (
        <img
          src={slide.poster}
          alt=""
          role="presentation"
          width={2400}
          height={1000}
          loading={eagerPoster ? "eager" : "lazy"}
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover object-center"
          data-testid={`hero-loop-poster-${slide.slug}`}
        />
      ) : null}
      {showVideo ? (
        <video
          ref={videoRef}
          src={slide.video ?? undefined}
          className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700 ease-out motion-reduce:transition-none ${
            ready ? "opacity-100" : "opacity-0"
          }`}
          autoPlay={role === "active"}
          muted
          loop
          playsInline
          preload={queuedPreload}
          poster={slide.poster ?? undefined}
          aria-hidden="true"
          tabIndex={-1}
          disablePictureInPicture
          disableRemotePlayback
          onPlaying={markReady}
          onCanPlay={markReady}
          onError={markFailed}
          data-testid={`hero-loop-video-${slide.slug}`}
          data-playback="loop"
        />
      ) : null}
      {showStill ? (
        <video
          src={slide.video ?? undefined}
          className="absolute inset-0 h-full w-full object-cover object-center"
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
          tabIndex={-1}
          disablePictureInPicture
          disableRemotePlayback
          onLoadedData={(event) => safePause(event.currentTarget)}
          onError={markFailed}
          data-testid={`hero-loop-video-${slide.slug}`}
          data-playback="still"
        />
      ) : null}
    </div>
  );
}

/**
 * Background media layer for the homepage hero. Renders nothing when there
 * are no slides so the caller's navy base shows. The caller owns
 * `activeIndex` so the caption / dots (`HomeHeroLoopCaption`) can live
 * inside the copy block, on top of the scrim.
 */
export function HomeHeroLoops({
  slides,
  activeIndex,
  onAdvance,
  autoRotate = true,
  className = "absolute inset-0",
  mediaClassName = "",
}: {
  slides: HomeHeroSlide[];
  activeIndex: number;
  /** Called every `HOME_HERO_ROTATE_MS` while rotating; must be referentially stable. */
  onAdvance: () => void;
  /** Set false once the visitor picks a project so the choice sticks. */
  autoRotate?: boolean;
  className?: string;
  mediaClassName?: string;
}) {
  const reducedMotion = usePrefersReducedMotion();
  const [constrained] = useState(() => isConstrainedConnection());
  const playVideo = shouldPlayHeroVideo({
    reducedMotion,
    constrainedConnection: constrained,
  });

  const rootRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(true);
  const count = slides.length;

  // Pause playback and rotation whenever the hero scrolls out of view.
  // `count` is a dependency because the first render often has no slides
  // (waiting on the projects payload), so the root node does not exist
  // yet — observing only on mount would never attach.
  useEffect(() => {
    const node = rootRef.current;
    if (count === 0 || !node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [count]);
  const safeIndex = count > 0 ? ((activeIndex % count) + count) % count : 0;
  const rotate = playVideo && autoRotate && count > 1;
  const nextIndex = rotate ? (safeIndex + 1) % count : -1;

  // Hold the previous slide at full opacity while the new one fades in on
  // top, then unmount it. A mutual opacity dip would flash the navy base.
  // Assign `leavingIndex` during render (not in useEffect) so the first
  // painted frame after `safeIndex` changes already keeps the outgoing
  // slide mounted — an effect would leave one empty frame first.
  const [trackedIndex, setTrackedIndex] = useState(safeIndex);
  const [leavingIndex, setLeavingIndex] = useState<number | null>(null);
  if (count === 0) {
    if (trackedIndex !== safeIndex || leavingIndex !== null) {
      setTrackedIndex(safeIndex);
      setLeavingIndex(null);
    }
  } else if (safeIndex !== trackedIndex) {
    setLeavingIndex(trackedIndex);
    setTrackedIndex(safeIndex);
  }
  useEffect(() => {
    if (leavingIndex === null) return;
    const id = window.setTimeout(() => setLeavingIndex(null), HOME_HERO_FADE_MS);
    return () => window.clearTimeout(id);
  }, [leavingIndex]);

  // Slides that have reported frames (or a terminal error). The timer only
  // crossfades to a poster-less slide once it is in this set, so the hero
  // never fades to an empty layer; a late arrival resolves the pending
  // advance immediately.
  const readyIds = useRef<Set<string>>(new Set());
  const pendingAdvance = useRef(false);
  const nextIdRef = useRef<string | null>(null);

  const handleReady = useCallback(
    (slideId: string) => {
      readyIds.current.add(slideId);
      // Only the queued slide can resolve a held crossfade. A late
      // `canplay` from the slide already on screen must not skip ahead.
      if (pendingAdvance.current && slideId === nextIdRef.current) {
        pendingAdvance.current = false;
        onAdvance();
      }
    },
    [onAdvance],
  );

  // The timer restarts whenever the active slide changes, so every slide
  // gets a full hold after a crossfade or a manual pick.
  useEffect(() => {
    pendingAdvance.current = false;
    if (!rotate || !inView) {
      nextIdRef.current = null;
      return;
    }
    const next = slides[(safeIndex + 1) % count];
    nextIdRef.current = next.id;
    const id = window.setInterval(() => {
      const canShow = !next.video || !!next.poster || readyIds.current.has(next.id);
      if (canShow) onAdvance();
      else pendingAdvance.current = true;
    }, HOME_HERO_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [rotate, inView, onAdvance, slides, safeIndex, count]);

  if (count === 0) return null;

  return (
    <div
      ref={rootRef}
      className={`overflow-hidden ${className} ${mediaClassName}`}
      aria-hidden="true"
      data-testid="hero-loops"
      data-playback={playVideo ? "loop" : "poster"}
    >
      {slides.map((slide, index) => {
        const active = index === safeIndex;
        const leaving = index === leavingIndex;
        // Mount the active slide, the outgoing slide during a crossfade,
        // and the one queued next (so it is buffered). Reduced motion /
        // Save-Data get the active poster's own frame alone.
        if (!active && !leaving && index !== nextIndex) return null;
        const role: SlideRole = active ? "active" : leaving ? "leaving" : "preload";
        return (
          <HomeHeroSlideLayer
            key={slide.id}
            slide={slide}
            role={role}
            fadeIn={active && leavingIndex !== null}
            playing={(active || leaving) && inView}
            primePlayback={role === "preload" && !slide.poster && inView}
            withVideo={playVideo}
            stillFrame={reducedMotion && !constrained && !slide.poster}
            eagerPoster={index === 0 || active}
            onReady={handleReady}
          />
        );
      })}
    </div>
  );
}

/**
 * Caption + dot controls for the hero loops: names the project on screen
 * (with its TEA classification as a text badge) and lets a visitor pick a
 * project directly. Rendered inside the copy block, above the scrim.
 *
 * The row always occupies the same fixed height — including before the
 * projects payload arrives and when there are no slides — so the hero never
 * grows when data lands.
 */
const CAPTION_SLOT_CLASS = "mt-10 flex h-9 items-center justify-center gap-3";

export function HomeHeroLoopCaption({
  slides,
  activeIndex,
  onSelect,
  projectHref,
}: {
  slides: HomeHeroSlide[];
  activeIndex: number;
  onSelect: (index: number) => void;
  /** Override the project link. Homepage keeps `/projects/:slug`. */
  projectHref?: (slug: string) => string;
}) {
  const count = slides.length;
  if (count === 0) {
    return <div className={CAPTION_SLOT_CLASS} aria-hidden="true" data-testid="hero-loop-caption-placeholder" />;
  }
  const safeIndex = ((activeIndex % count) + count) % count;
  const active = slides[safeIndex];
  return (
    <div className={CAPTION_SLOT_CLASS} data-testid="hero-loop-caption">
      <Link
        href={projectHref ? projectHref(active.slug) : `/projects/${active.slug}`}
        className="group inline-flex min-w-0 max-w-[75vw] items-center gap-2 rounded-full border border-white/20 bg-black/30 px-3.5 py-1.5 text-xs text-white/85 backdrop-blur-sm transition-colors hover:bg-black/45 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 sm:max-w-none"
        data-testid={`link-hero-loop-project-${active.slug}`}
      >
        <span className="sr-only">Now showing: </span>
        <span className="truncate font-semibold tracking-wide" data-testid="text-hero-loop-title">
          {active.title}
        </span>
        {active.location ? (
          <span className="hidden truncate text-white/60 sm:inline" data-testid="text-hero-loop-location">
            {active.location}
          </span>
        ) : null}
        {active.statusLabel ? (
          <span className="hidden shrink-0 text-white/75 sm:inline" data-testid="text-hero-loop-status">
            {active.statusLabel}
          </span>
        ) : null}
        {active.classification ? (
          <span
            className="shrink-0 rounded-full bg-[#C9A84C] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#0A2540]"
            data-testid="badge-hero-loop-classification"
          >
            {active.classification}
          </span>
        ) : null}
      </Link>
      {count > 1 ? (
        <div className="flex shrink-0 items-center gap-2" role="group" aria-label="Project loops">
          {slides.map((slide, index) => {
            const selected = index === safeIndex;
            return (
              <button
                key={slide.id}
                type="button"
                onClick={() => onSelect(index)}
                aria-label={`Show ${slide.title}`}
                aria-pressed={selected}
                className={`h-2.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 ${
                  selected ? "w-7 bg-[#C9A84C]" : "w-2.5 bg-white/40 hover:bg-white/70"
                }`}
                data-testid={`button-hero-loop-dot-${slide.slug}`}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
